import type { CaravanDb } from './db'
import {
  entityKey,
  type EntityType,
  type LocalEntity,
  type MutationOperation,
} from '../../domain/sync'
import { signalLocalMutation } from '../../services/syncSignals'

export type LocalMutationInput = {
  mutationId: string
  entityType: EntityType
  entityId: string
  tripId: string
  operation: MutationOperation
  baseVersion: number
  payload: Record<string, unknown>
  createdAtDevice: string
  requiredBytes?: number
}

type StorageEstimate = { quota?: number; usage?: number }
type RepositoryOptions = {
  estimateStorage?: () => Promise<StorageEstimate>
}

export class LocalQuotaError extends Error {
  constructor() {
    super('Stockage local insuffisant. Libérez de l’espace sans supprimer vos contributions en attente.')
    this.name = 'LocalQuotaError'
  }
}

export class MutationIdCollisionError extends Error {
  constructor() {
    super('Cet identifiant de mutation appartient déjà à une autre contribution.')
    this.name = 'MutationIdCollisionError'
  }
}

export class ArchivedTripError extends Error {
  constructor() {
    super('Voyage terminé · lecture seule. Cette modification n’est plus autorisée.')
    this.name = 'ArchivedTripError'
  }
}

export async function assertTripWritable(db: CaravanDb, tripId: string): Promise<void> {
  const archive = await db.tripArchives.get(tripId)
  if (archive?.status === 'archived') throw new ArchivedTripError()
}

export class LocalRepository {
  readonly #db: CaravanDb
  readonly #estimateStorage: () => Promise<StorageEstimate>

  constructor(db: CaravanDb, options: RepositoryOptions = {}) {
    this.#db = db
    this.#estimateStorage =
      options.estimateStorage ??
      (async () => {
        return navigator.storage.estimate()
      })
  }

  async mutate(input: LocalMutationInput): Promise<LocalEntity> {
    await assertTripWritable(this.#db, input.tripId)
    if ((input.requiredBytes ?? 0) > 0) {
      const { quota, usage } = await this.#estimateStorage()
      if (quota !== undefined && usage !== undefined && quota - usage < (input.requiredBytes ?? 0)) {
        throw new LocalQuotaError()
      }
    }

    const key = entityKey(input.entityType, input.entityId)
    const entity = await this.#db.transaction('rw', this.#db.entities, this.#db.outbox, async () => {
      const duplicate = await this.#db.outbox.get(input.mutationId)
      if (duplicate) {
        if (
          duplicate.entityId !== input.entityId ||
          duplicate.entityType !== input.entityType ||
          duplicate.operation !== input.operation
        ) {
          throw new MutationIdCollisionError()
        }
        const existing = await this.#db.entities.get(key)
        if (!existing) throw new Error('Mutation présente sans entité locale.')
        return existing
      }

      const previous = await this.#db.entities.get(key)
      const entity: LocalEntity = {
        key,
        id: input.entityId,
        tripId: input.tripId,
        entityType: input.entityType,
        payload: structuredClone(input.payload),
        createdAtDevice: previous?.createdAtDevice ?? input.createdAtDevice,
        updatedAtDevice: input.createdAtDevice,
        updatedAtServer: previous?.updatedAtServer ?? null,
        version: input.baseVersion,
        deletedAt: input.operation === 'delete' ? input.createdAtDevice : null,
        lastMutationId: input.mutationId,
        syncState: 'pending',
        lastError: null,
      }
      await this.#db.entities.put(entity)
      await this.#db.outbox.add({
        mutationId: input.mutationId,
        entityType: input.entityType,
        entityId: input.entityId,
        tripId: input.tripId,
        operation: input.operation,
        baseVersion: input.baseVersion,
        payload: structuredClone(input.payload),
        createdAtDevice: input.createdAtDevice,
        state: 'pending',
        attemptCount: 0,
        nextAttemptAt: 0,
        lastErrorCode: null,
        lastErrorMessage: null,
      })
      return entity
    })
    signalLocalMutation()
    return entity
  }
}
